import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, FormHelperText, Stack } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    DialogTitleWithClose,
    formatDateString,
    getDefaultSupportTypes,
    IOrganisation,
    ISupportPeriod,
    ISupportPeriodForm,
    StatusCode,
    useOrganisations,
    useSupportTypes,
} from '../../../shared';
import { useSaveSupportPeriod } from '../../hooks';
import { newSupportPeriodMapperToForm, supportPeriodMapperFromForm, supportPeriodMapperToForm } from '../../mappers';
import { useSupportPeriodSchema } from '../../validators';
import { SupportTypesInfo } from '../support-types-info/support-types-info.component';
import { ReferrerInputs } from './referrer-inputs.component';
import { SupportPeriodGeneralInputs } from './support-period-general-inputs.component';
import { AxiosError, AxiosResponse } from 'axios';

interface Props {
    show: boolean;
    supportCase: string;
    supportPeriod: ISupportPeriod | null;
    handleClose: () => void;
    organisation: IOrganisation;
}

export const SaveSupportPeriodModal: FC<Props> = ({ show, supportCase, supportPeriod, handleClose, organisation }) => {
    const { t } = useTranslation();
    const [duplicateError, setDuplicateError] = useState<string | undefined>(undefined);
    const defaultSupportTypes = getDefaultSupportTypes(organisation);
    const organisations = useOrganisations();

    const { mutateAsync: saveSupportPeriod, isPending } = useSaveSupportPeriod();

    const schema = useSupportPeriodSchema();
    const form = useForm<ISupportPeriodForm>({
        resolver: yupResolver(schema),
        mode: 'onSubmit',
    });

    const watchOrganisation = form.watch('organisation');

    useEffect(() => {
        if (watchOrganisation && watchOrganisation?.id !== supportPeriod?.organisationId) {
            form.setValue(
                'supportTypesInfo',
                getDefaultSupportTypes(watchOrganisation).map((id) => ({ supportTypeId: id })),
            );
        }
    }, [form, watchOrganisation, supportPeriod]);

    const supportTypesList = useSupportTypes();
    const supportTypes = useMemo(
        () => watchOrganisation?.supportTypes || supportTypesList,
        [watchOrganisation, supportTypesList],
    );

    useEffect(() => {
        if (organisations.length && show) {
            if (supportPeriod) form.reset({ ...supportPeriodMapperToForm(supportPeriod, organisations) });
            else form.reset(newSupportPeriodMapperToForm(organisation));
        }
    }, [form, supportPeriod, organisation, organisations, show]);

    const onHandleClose = () => {
        setDuplicateError(undefined);
        form.clearErrors();
        handleClose();
    };

    const onSubmit = async (data: ISupportPeriodForm) => {
        try {
            await saveSupportPeriod({
                supportPeriod: supportPeriodMapperFromForm({ ...supportPeriod, ...data }),
                supportCase: supportCase,
            });
            onHandleClose();
        } catch (err) {
            if ((err as AxiosError)?.response?.status === StatusCode.CONFLICT) {
                const { data } = (err as AxiosError)?.response?.data as AxiosResponse<{
                    startDate: string;
                    endDate: string;
                }>;

                form.setError('startDate', {});
                form.setError('endDate', {});
                setDuplicateError(
                    t('duplicateSupportPeriodError', {
                        start: formatDateString(data?.startDate),
                        end: formatDateString(data?.endDate),
                        interpolation: { escapeValue: false },
                    }),
                );
            } else {
                throw err;
            }
        }
    };
    return (
        <Dialog open={show} onClose={onHandleClose} fullWidth maxWidth="md">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <DialogTitleWithClose onClose={onHandleClose}>{t('addSupportPeriod')}</DialogTitleWithClose>
                    <DialogContent>
                        <Stack spacing={2}>
                            <SupportPeriodGeneralInputs organisations={organisations} />
                            <SupportTypesInfo supportTypes={supportTypes} defaultSupportTypes={defaultSupportTypes} />
                            <ReferrerInputs supportPeriod={supportPeriod} />
                        </Stack>
                        {duplicateError !== undefined && (
                            <FormHelperText sx={{ color: (theme) => theme.palette.error.main }}>
                                {duplicateError || t('shared.form.fallbackError')}
                            </FormHelperText>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="contained" color="primary" disabled={isPending}>
                            {t('save')}
                        </Button>
                        <Button onClick={onHandleClose} color="secondary" disabled={isPending}>
                            {t('cancel')}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
